/* iter55z-m — UnitEconomicsPanel: per-user + per-plan revenue / COGS
 * / margin at a glance.
 *
 * Powered by `GET /api/admin/unit-economics?from=…&to=…`. Presets:
 *   • Today       — midnight → now
 *   • MTD         — 1st of month → now
 *   • Last 30 d   — now-30 → now
 *   • Custom      — free-form date pickers
 *
 * The per-user table is sorted by margin ascending, so the biggest
 * cost centres surface first — that's the row an admin usually wants
 * to click to investigate (heavy AI usage on a free tier, etc.).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { PanelHeader } from './_shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, RefreshCw, TrendingUp, TrendingDown, Users, Coins, DollarSign, FileDown } from 'lucide-react';
import PerNovelEconomicsCard from './PerNovelEconomicsCard';   // Feb-2026 revenue lifts
import MarketingRoiCard from './MarketingRoiCard';             // Sep-2026 Google Ads CAC/LTV
import RedditClickIdDashboard from './RedditClickIdDashboard';  // Oct-2026 Click-ID dashboard
import CacheStatsCard from './CacheStatsCard';                  // Aug-2026 prompt-cache savings

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'mtd',   label: 'MTD'   },
  { id: '30d',   label: 'Last 30 d' },
  { id: 'custom', label: 'Custom…' },
];

function _iso(d) { return d.toISOString().slice(0, 10); }

function _rangeFor(preset) {
  const now = new Date();
  const to  = _iso(now);
  if (preset === 'today') return { from: to, to };
  if (preset === 'mtd') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: _iso(first), to };
  }
  // default 30d
  const past = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
  return { from: _iso(past), to };
}

const usd = (n) => `$${Number(n || 0).toFixed(Math.abs(Number(n || 0)) < 10 ? 2 : 2)}`;
const usdT = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function MarginPill({ value }) {
  const positive = value >= 0;
  return (
    <span
      data-testid="ue-margin-pill"
      className={`inline-flex items-center gap-1 rounded-full px-2 py-[1px] text-[10.5px] font-semibold ${
        positive ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
      }`}
    >
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {usd(value)}
    </span>
  );
}

function KpiCard({ label, value, hint, icon: Icon, testId }) {
  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="mt-1 font-mono-stat text-[19px] font-semibold">{value}</div>
      {hint && <div className="mt-0.5 text-[10.5px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

export default function UnitEconomicsPanel() {
  const [preset, setPreset] = useState('30d');
  const [range, setRange]   = useState(() => _rangeFor('30d'));
  const [busy, setBusy]     = useState(true);
  const [data, setData]     = useState(null);
  const [customFrom, setCustomFrom] = useState(range.from);
  const [customTo,   setCustomTo]   = useState(range.to);

  const load = useCallback(async (r) => {
    setBusy(true);
    try {
      const q = new URLSearchParams({ from: r.from, to: r.to }).toString();
      const res = await axios.get(`${API}/admin/unit-economics?${q}`,
        { withCredentials: true });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(range); }, [range, load]);

  // iter55z-q — First-time contextual tour for the Unit Economics panel.
  useEffect(() => {
    const t = setTimeout(() => {
      import('@/lib/tours').then(({ runTour }) => runTour('unit-economics-open'));
    }, 800);
    return () => clearTimeout(t);
  }, []);

  const applyPreset = (id) => {
    setPreset(id);
    if (id !== 'custom') {
      const r = _rangeFor(id);
      setRange(r); setCustomFrom(r.from); setCustomTo(r.to);
    }
  };
  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    setPreset('custom');
    setRange({ from: customFrom, to: customTo });
  };

  const totals   = data?.totals   || {};
  const perUser  = useMemo(() => data?.per_user || [], [data]);
  const perPlan  = data?.per_plan || [];

  const negativeCount = useMemo(
    () => perUser.filter((r) => (r.margin_usd || 0) < 0).length,
    [perUser],
  );

  return (
    <section data-testid="admin-unit-economics-panel">
      <PanelHeader title="Unit Economics" />
      <p className="mb-4 text-[11.5px] text-muted-foreground">
        Per-user and per-plan revenue vs. AI-provider COGS. Rows are sorted by
        margin ascending — the biggest cost centres surface first. Revenue comes
        from paid Stripe transactions; COGS comes from recorded provider bills
        (<code className="font-mono-stat">cost_events</code>).
      </p>

      {/* Period selector */}
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="flex items-center gap-1 rounded-md border border-border bg-card p-0.5">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p.id)}
              data-testid={`ue-preset-${p.id}`}
              className={`rounded-sm px-2.5 py-1 text-[11px] transition ${
                preset === p.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="flex items-end gap-2" data-testid="ue-custom-range">
            <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                    className="h-8 w-[140px] text-[12px]" data-testid="ue-custom-from" />
            <span className="pb-1.5 text-[11px] text-muted-foreground">→</span>
            <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                    className="h-8 w-[140px] text-[12px]" data-testid="ue-custom-to" />
            <Button size="sm" onClick={applyCustom} className="h-8 text-[11px]" data-testid="ue-apply-custom">Apply</Button>
          </div>
        )}
        <Button
          size="sm" variant="ghost" onClick={() => load(range)}
          className="ml-auto h-8 gap-1 text-[11px]"
          data-testid="ue-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <Button
          size="sm" variant="outline"
          onClick={() => {
            // iter55z-o — CSV export of the per-user table (biggest data
            // set; per-plan is trivially derivable from it).
            const rows = [
              ['user_id','email','name','tier','tokens_charged','revenue_usd','cogs_usd','margin_usd','margin_pct'],
              ...perUser.map((r) => [
                r.user_id, r.email, r.name, r.tier,
                r.tokens_charged, r.revenue_usd, r.cogs_usd, r.margin_usd, r.margin_pct,
              ]),
            ];
            const csv = rows.map((r) => r.map((v) => {
              const s = String(v ?? '');
              return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            }).join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `unit-economics_${range.from}_to_${range.to}.csv`;
            document.body.appendChild(a); a.click();
            setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 800);
          }}
          className="h-8 gap-1 text-[11px]"
          data-testid="ue-csv-export"
          disabled={busy || perUser.length === 0}
        >
          <FileDown className="h-3.5 w-3.5" /> CSV
        </Button>
      </div>

      {/* KPI row */}
      <div className="mb-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
        <KpiCard
          label="Revenue"
          value={usdT(totals.revenue_usd)}
          hint={`${totals.active_users || 0} paying / active users`}
          icon={DollarSign}
          testId="ue-kpi-revenue"
        />
        <KpiCard
          label="COGS"
          value={usdT(totals.cogs_usd)}
          hint={`${(totals.tokens_charged || 0).toLocaleString()} tokens charged`}
          icon={Coins}
          testId="ue-kpi-cogs"
        />
        <KpiCard
          label="Gross margin"
          value={usdT(totals.margin_usd)}
          hint={`${totals.margin_pct ?? 0}% of revenue`}
          icon={TrendingUp}
          testId="ue-kpi-margin"
        />
        <KpiCard
          label="Loss-making users"
          value={negativeCount}
          hint="COGS exceeded revenue in this period"
          icon={Users}
          testId="ue-kpi-loss-users"
        />
      </div>

      {/* Per-plan aggregate */}
      <div className="mb-4 rounded-md border border-border bg-card" data-testid="ue-per-plan">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-[11.5px] font-semibold">By plan tier</span>
          <span className="text-[10.5px] text-muted-foreground">{perPlan.length} tier{perPlan.length === 1 ? '' : 's'}</span>
        </div>
        {busy && (
          <div className="p-4 text-center text-[12px] text-muted-foreground">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" />
          </div>
        )}
        {!busy && perPlan.length === 0 && (
          <div className="p-4 text-center text-[12px] text-muted-foreground">No activity in this period.</div>
        )}
        {!busy && perPlan.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5 text-left">Tier</th>
                  <th className="px-3 py-1.5 text-right">Users</th>
                  <th className="px-3 py-1.5 text-right">Tokens</th>
                  <th className="px-3 py-1.5 text-right">Revenue</th>
                  <th className="px-3 py-1.5 text-right">COGS</th>
                  <th className="px-3 py-1.5 text-right">Margin</th>
                  <th className="px-3 py-1.5 text-right">Margin %</th>
                </tr>
              </thead>
              <tbody>
                {perPlan.map((p) => (
                  <tr key={p.tier} className="border-t border-border/60" data-testid={`ue-plan-row-${p.tier}`}>
                    <td className="px-3 py-1.5 font-mono-stat font-semibold capitalize">{p.tier}</td>
                    <td className="px-3 py-1.5 text-right">{p.users}</td>
                    <td className="px-3 py-1.5 text-right font-mono-stat">{Number(p.tokens_charged || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right font-mono-stat">{usdT(p.revenue_usd)}</td>
                    <td className="px-3 py-1.5 text-right font-mono-stat text-muted-foreground">{usdT(p.cogs_usd)}</td>
                    <td className="px-3 py-1.5 text-right"><MarginPill value={p.margin_usd} /></td>
                    <td className="px-3 py-1.5 text-right font-mono-stat">{p.margin_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Per-user table */}
      <div className="rounded-md border border-border bg-card" data-testid="ue-per-user">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-[11.5px] font-semibold">By user</span>
          <span className="text-[10.5px] text-muted-foreground">{perUser.length} user{perUser.length === 1 ? '' : 's'} · sorted by margin ↑</span>
        </div>
        {!busy && perUser.length === 0 && (
          <div className="p-4 text-center text-[12px] text-muted-foreground">No user activity in this period.</div>
        )}
        {!busy && perUser.length > 0 && (
          <div className="max-h-[520px] overflow-y-auto overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5 text-left">User</th>
                  <th className="px-3 py-1.5 text-left">Tier</th>
                  <th className="px-3 py-1.5 text-right">Tokens</th>
                  <th className="px-3 py-1.5 text-right">Revenue</th>
                  <th className="px-3 py-1.5 text-right">COGS</th>
                  <th className="px-3 py-1.5 text-right">Margin</th>
                  <th className="px-3 py-1.5 text-right">Margin %</th>
                </tr>
              </thead>
              <tbody>
                {perUser.map((r) => (
                  <tr key={r.user_id} className="border-t border-border/60" data-testid={`ue-user-row-${r.user_id}`}>
                    <td className="px-3 py-1.5">
                      <div className="flex flex-col leading-tight">
                        <span className="text-[12px] font-medium">
                          {r.name || r.email || r.user_id}
                          {r.suggest_downgrade && (
                            <span
                              title="Loss-making with no revenue — consider downgrading"
                              data-testid={`ue-downgrade-hint-${r.user_id}`}
                              className="ml-1.5 rounded-sm border border-amber-400 bg-amber-50 px-1 text-[9px] font-semibold uppercase tracking-wider text-amber-900"
                            >
                              suggest ↓
                            </span>
                          )}
                        </span>
                        {r.email && r.name && <span className="text-[10.5px] text-muted-foreground">{r.email}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 font-mono-stat capitalize">{r.tier}</td>
                    <td className="px-3 py-1.5 text-right font-mono-stat">{Number(r.tokens_charged || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right font-mono-stat">{usdT(r.revenue_usd)}</td>
                    <td className="px-3 py-1.5 text-right font-mono-stat text-muted-foreground">{usdT(r.cogs_usd)}</td>
                    <td className="px-3 py-1.5 text-right"><MarginPill value={r.margin_usd} /></td>
                    <td className="px-3 py-1.5 text-right font-mono-stat">{r.margin_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Feb-2026 · Per-novel P&L + volume-to-target projection card. */}
      <PerNovelEconomicsCard />
      {/* Aug-2026 · Prompt-cache savings across all providers. */}
      <CacheStatsCard from={range.from} to={range.to} />
      {/* Sep-2026 · Google Ads / channel CAC vs LTV tracker. */}
      <MarketingRoiCard />
      <RedditClickIdDashboard />
    </section>
  );
}
